import React, { useCallback, useEffect, useState } from 'react';
import { Wrench, X } from 'lucide-react';
import { mirror, isSupported } from './runtime';

/**
 * The visible proof that this page is agent-ready.
 *
 * A site can register perfect tools and look identical to one that registered
 * none, which is a bad deal for the humans deciding whether to trust it. This
 * badge shows what an agent can see right now, grouped by when each tool
 * becomes available, and marks the ones that can change something or spend
 * money. It reads the same registry the agent does, so it cannot drift.
 */

const GROUPS = [
  ['public', 'ALWAYS AVAILABLE'],
  ['account', 'WHEN SIGNED IN'],
  ['page', 'ON THIS PAGE'],
];

export default function WebMcpBadge() {
  const [open, setOpen] = useState(false);
  const [tools, setTools] = useState([]);

  const refresh = useCallback(() => {
    const m = mirror();
    setTools(m ? m.list() : []);
  }, []);

  useEffect(() => {
    const m = mirror();
    refresh();
    return m && m.onChange ? m.onChange(refresh) : undefined;
  }, [refresh]);

  if (!tools.length) return null;

  const native = isSupported();
  const consequential = (t) => t.annotations && t.annotations.consequentialHint;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={`WebMCP: ${tools.length} agent tools on this page`}
        className="fixed bottom-4 right-4 z-40 mono inline-flex items-center gap-2 rounded-full border border-[var(--border-2)] bg-[var(--surface)]/95 backdrop-blur px-3 py-1.5 text-[0.66rem] tracking-[0.12em] uppercase text-[var(--text-3)] hover:text-[var(--accent)] hover:border-[var(--accent)]/50 transition-colors shadow-lg"
      >
        <Wrench size={12} className="text-[var(--accent)]" />
        WebMCP · {tools.length} tools
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="WebMCP tools on this page"
          className="fixed bottom-16 right-4 z-40 w-[min(92vw,30rem)] max-h-[70vh] overflow-y-auto rounded-sm border border-[var(--border-2)] bg-[var(--bg-2)] shadow-2xl"
        >
          <div className="flex items-start justify-between gap-4 px-5 pt-4 pb-3 border-b border-[var(--border)]">
            <div>
              <div className="serif text-[1.15rem] text-[var(--text)] leading-none">WebMCP tools</div>
              <p className="text-[0.76rem] text-[var(--text-3)] mt-1.5 leading-[1.5]">
                What an AI agent can do on this page without guessing at the interface.
              </p>
            </div>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close"
              className="text-[var(--text-3)] hover:text-[var(--text)] transition-colors shrink-0">
              <X size={16} />
            </button>
          </div>

          <div className="px-5 py-4 space-y-4">
            {GROUPS.map(([key, label]) => {
              const inGroup = tools.filter((t) => (t.group || 'public') === key);
              if (!inGroup.length) return null;
              return (
                <div key={key}>
                  <div className="mono text-[0.6rem] tracking-[0.18em] uppercase text-[var(--text-4)] mb-2">
                    {label}
                  </div>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                    {inGroup.map((t) => (
                      <li key={t.name} className="mono text-[0.72rem] text-[var(--text-2)] flex items-center gap-2" title={t.description}>
                        <span className={consequential(t) ? 'text-[var(--rust)]' : 'text-[var(--accent)]'}>•</span>
                        <span className="truncate">{t.name}</span>
                        {consequential(t) && (
                          <span className="text-[var(--rust)] text-[0.62rem] shrink-0" title="Can change something or spend money">✎</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          <div className="px-5 py-3 border-t border-[var(--border)] mono text-[0.6rem] tracking-[0.08em] text-[var(--text-4)] leading-[1.7]">
            <span className="text-[var(--accent)]">•</span> reads only
            {'   '}
            <span className="text-[var(--rust)]">✎</span> can change things or spend money
            <div className="mt-1">
              {native
                ? 'Registered with this browser through document.modelContext.'
                : 'This browser has no WebMCP support yet, so the tools are exposed on the page for agents that can call them directly.'}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
